import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import OsScroll from './OsScroll';

/**
 * Custom dropdown aligned with env-picker / method-picker (rounded border, hover rows, dark mode).
 *
 * `searchable` opts a picker into a live-filter text box at the top of the
 * menu — off by default so small pickers (method, env, ...) keep their
 * current look; turn it on for longer option lists (e.g. the chat code
 * language picker).
 */
export default function CompactSelectPicker({
  value,
  onChange,
  options = [],
  disabled = false,
  className = '',
  /**
   * Extra class on the PORTALED menu. The menu is rendered into document.body,
   * so a caller cannot reach it with a descendant selector from its own
   * wrapper — without this, restyling the menu for one call site means
   * restyling it for all ~70.
   */
  menuClassName = '',
  menuMinWidth = 160,
  searchable = false,
  searchPlaceholder = 'Search…',
  placeholder = null,
  /**
   * An action rendered under the list, inside the menu — "Edit options…" and
   * the like. Given a `close` callback, because an action that opens something
   * else has to put the menu away first. Rendering it OUTSIDE the list keeps it
   * out of the search filter and out of the option roles, where a button that
   * is not one of the choices does not belong.
   */
  renderFooter = null,
  onOpenChange,
  /**
   * Opens the menu from outside without a click — the Documents workspace
   * switcher opens while a tree row is dragged over it. The caller resets it
   * from `onOpenChange(false)`.
   */
  forceOpen = false,
  /**
   * Extra props for one option's row button (drag and drop handlers, a
   * data-* hook) and for the trigger button. Both default to none, so the
   * ~70 existing call sites render exactly as before.
   */
  rowProps = null,
  triggerProps = null,
  'aria-label': ariaLabel = 'Select option',
}) {
  const [openState, setOpen] = useState(false);
  const open = openState || forceOpen;
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const match = options.find((o) => o.value === value);

  // `|| options[0]` is the historical fallback and stays, because ~70 call sites
  // pass an enum whose value always matches and rely on it. But it is a lie
  // whenever the value CAN be empty: the trigger renders the first option as
  // though it were chosen, the user reasonably believes it is, and submitting
  // fails validation on a field that looks filled in. That is what "Pick a team
  // for this workspace" over a visibly selected team was.
  //
  // Callers whose value can legitimately be unset pass `placeholder`, and get
  // told the truth instead.
  const selected = match || (placeholder ? null : options[0]);
  const label = selected?.label ?? (placeholder && !match ? placeholder : value ?? '');
  const showingPlaceholder = !match && !!placeholder;
  // `searchText` exists for options whose visible label is not what you would
  // type to find them — the assignee picker labels your own row "Me", but
  // typing your actual name must still match it.
  const filteredOptions =
    searchable && query.trim()
      ? options.filter((o) =>
          (o.searchText ?? o.label ?? '').toLowerCase().includes(query.trim().toLowerCase())
        )
      : options;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    onOpenChange?.(false);
  }, [onOpenChange]);

  // Open below by default; flip above when there isn't room below but
  // there's more room above (e.g. a picker anchored near the bottom of
  // the window, like the chat composer's code-language picker).
  const widthFn = useCallback((r) => Math.max(r.width, menuMinWidth), [menuMinWidth]);
  // `value` can change externally while the popover is open (e.g. a realtime
  // collab echo updating the request's body type/language) — the trigger's
  // rendered width shifts with its label text, so force a re-measure on that
  // change too, not just on open/resize/scroll.
  const pos = usePopoverPosition({
    anchorRef: triggerRef,
    open,
    width: widthFn,
    placement: 'auto',
    deps: [value],
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  const pick = (nextValue) => {
    onChange?.(nextValue);
    close();
  };

  const toggle = () => {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      pick(filteredOptions[0].value);
    }
  };

  const dropdown =
    typeof document !== 'undefined' && document.body && open
      ? createPortal(
          <>
            <div
              className="auth-picker-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                close();
              }}
            />
            <div
              className={`auth-picker__menu ${menuClassName}`.trim()}
              role="listbox"
              style={{
                position: 'fixed',
                ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
                left: pos.left,
                minWidth: pos.width,
                maxHeight: pos.maxHeight,
                zIndex: 10040,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {searchable && (
                <div className="auth-picker__search">
                  <Search size={12} className="auth-picker__search-icon" aria-hidden />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    placeholder={searchPlaceholder}
                    className="auth-picker__search-input"
                    aria-label="Filter options"
                  />
                </div>
              )}
              {/* The app's scrollbar, not the browser's (owner, 13.10.2026: the
                  labels dropdown) — same shape as SearchableSelect. minHeight 0
                  lets it shrink when the menu is clamped near a screen edge. */}
              <OsScroll style={{ maxHeight: '16rem', minHeight: 0 }}>
              <div className="auth-picker__list auth-picker__list--os">
                {filteredOptions.length === 0 && (
                  <div className="auth-picker__empty">No matches</div>
                )}
                {filteredOptions.map((opt, i) => {
                  const active = opt.value === value;
                  // Options may come in groups (`group`, optional
                  // `groupLabel`): a new group draws a hairline, and its label
                  // when it has one — a heading, not an option.
                  const prev = filteredOptions[i - 1];
                  const newGroup = opt.group != null && (!prev || prev.group !== opt.group);
                  return (
                    <React.Fragment key={opt.value}>
                    {newGroup && i > 0 && <div className="auth-picker__divider" role="separator" />}
                    {newGroup && opt.groupLabel && (
                      <div className="auth-picker__group" role="presentation">
                        {opt.groupLabel}
                      </div>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`auth-picker__row ${active ? 'auth-picker__row--active' : ''}`}
                      onClick={() => pick(opt.value)}
                      {...(rowProps ? rowProps(opt) : null)}
                    >
                      {/* `leading` is an optional node rendered before the
                          label — an avatar in the assignee picker. Kept
                          separate from `label` so the label stays a plain
                          string for search and for the trigger. */}
                      {opt.leading != null && (
                        <span className="auth-picker__row-leading shrink-0" aria-hidden>
                          {opt.leading}
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-left">{opt.label}</span>
                      {active && (
                        <Check size={12} className="auth-picker__icon shrink-0" aria-hidden />
                      )}
                    </button>
                    </React.Fragment>
                  );
                })}
              </div>
              </OsScroll>
              {renderFooter && (
                <div className="auth-picker__footer">
                  {renderFooter({ close })}
                </div>
              )}
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <>
      <div className={`auth-picker ${className}`.trim()} ref={triggerRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={toggle}
          className="auth-picker__trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          {...triggerProps}
        >
          {selected?.leading != null && (
            <span className="auth-picker__row-leading shrink-0" aria-hidden>
              {selected.leading}
            </span>
          )}
          <span
            className="auth-picker__trigger-label"
            data-placeholder={showingPlaceholder ? '' : undefined}
          >
            {label}
          </span>
          <ChevronDown
            size={14}
            className={`auth-picker__chevron ${open ? 'auth-picker__chevron--open' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {dropdown}
    </>
  );
}
